import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
    CircularProgress,
    FormControlLabel,
    Switch,
    RadioGroup,
    Radio,
    FormControl,
    FormLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../../../../../config/axiosConfig';
import PersianDatePicker from '../../../../common/PersianDatePicker';
import SearchableAutocomplete from '../../../../SearchableAutocomplete';
import { formatNumberInputValue, parseNumberInput } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';

function TeacherSalaryForm({ salary, defaultPersonType = 'teacher', onSuccess, onCancel }) {
    const theme = useTheme();
    const [personType, setPersonType] = useState(defaultPersonType); // 'teacher' or 'staff'
    const [formData, setFormData] = useState({
        person: null,
        person_id: null,
        fixed_monthly_salary: '',
        start_date: '',
        end_date: '',
        is_active: true,
        notes: '',
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (salary) {
            // Edit mode - populate form with existing data
            // Determine if this is a teacher or staff based on available data
            // Check if staff field exists and has a value
            const isStaff = salary.staff !== null && salary.staff !== undefined;
            const personName = isStaff ? salary.staff_name : salary.teacher_name;
            const personId = isStaff ? salary.staff : salary.teacher;
            
            setPersonType(isStaff ? 'staff' : 'teacher');

            // Construct option object matching API response structure
            const personOption = personId && personName
                ? {
                    id: personId, // This can be user ID for compatibility
                    user: {
                        id: personId,
                        first_name: personName?.split(' ')[0] || '',
                        last_name: personName?.split(' ').slice(1).join(' ') || '',
                    }
                }
                : null;

            setFormData({
                person: personOption,
                person_id: personId || null,
                fixed_monthly_salary: salary.fixed_monthly_salary || '',
                start_date: salary.start_date || '',
                end_date: salary.end_date || '',
                is_active: salary.is_active !== undefined ? salary.is_active : true,
                notes: salary.notes || '',
            });
        } else {
            // Create mode - reset form (default to the active tab's person type)
            setPersonType(defaultPersonType);
            setFormData({
                person: null,
                person_id: null,
                fixed_monthly_salary: '',
                start_date: '',
                end_date: '',
                is_active: true,
                notes: '',
            });
        }
    }, [salary, defaultPersonType]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handlePersonTypeChange = (newType) => {
        setPersonType(newType);
        // Clear person selection when switching types
        setFormData(prev => ({ ...prev, person: null, person_id: null }));
        if (errors.person) {
            setErrors(prev => ({ ...prev, person: null }));
        }
    };

    const handlePersonChange = (personId, personOption) => {
        handleChange('person_id', personId);
        handleChange('person', personOption);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.person_id) {
            newErrors.person = personType === 'teacher' 
                ? 'انتخاب آموزگار الزامی است' 
                : 'انتخاب پرسنل الزامی است';
        }

        if (!formData.fixed_monthly_salary || parseFloat(formData.fixed_monthly_salary) <= 0) {
            newErrors.fixed_monthly_salary = 'مبلغ حقوق ماهیانه باید بیشتر از صفر باشد';
        }

        if (!formData.start_date) {
            newErrors.start_date = 'تاریخ شروع الزامی است';
        }

        if (formData.end_date && formData.start_date) {
            const start = new Date(formData.start_date);
            const end = new Date(formData.end_date);
            if (end < start) {
                newErrors.end_date = 'تاریخ پایان باید بعد از تاریخ شروع باشد';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                // Use teacher field for teachers, staff field for staff
                // Backend will need to support staff field, but for now we'll use teacher
                [personType === 'teacher' ? 'teacher' : 'staff']: formData.person_id,
                // Value is already stored as integer (number) from parseNumberInput
                fixed_monthly_salary: typeof formData.fixed_monthly_salary === 'number' 
                    ? formData.fixed_monthly_salary 
                    : parseInt(String(formData.fixed_monthly_salary), 10) || 0,
                start_date: formData.start_date, // Already in YYYY-MM-DD format
                end_date: formData.end_date || null, // Already in YYYY-MM-DD format or null
                is_active: formData.is_active,
                notes: formData.notes || '',
            };

            if (salary) {
                // Update existing
                await api.patch(`${API.teacherInstituteSalaries}${salary.id}/`, payload);
            } else {
                // Create new
                await api.post(API.teacherInstituteSalaries, payload);
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving salary:', error);
            const errorData = error.response?.data || {};
            
            // Set field-specific errors
            const newErrors = {};
            Object.keys(errorData).forEach(key => {
                if (Array.isArray(errorData[key])) {
                    newErrors[key] = errorData[key][0];
                } else if (typeof errorData[key] === 'string') {
                    newErrors[key] = errorData[key];
                }
            });
            setErrors(newErrors);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend">نوع پرسنل</FormLabel>
                        <RadioGroup
                            row
                            value={personType}
                            onChange={(e) => handlePersonTypeChange(e.target.value)}
                        >
                            <FormControlLabel value="teacher" control={<Radio />} label="آموزگار" />
                            <FormControlLabel value="staff" control={<Radio />} label="پرسنل" />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <SearchableAutocomplete
                        label={personType === 'teacher' ? 'آموزگار' : 'پرسنل'}
                        required
                        apiUrl={personType === 'teacher' 
                            ? "/accounts/api/v1/profile/teachers/" 
                            : "/accounts/api/v1/profile/staffs/"}
                        value={formData.person_id}
                        initialOption={formData.person}
                        onChange={(value, option) => handlePersonChange(value, option)}
                        error={!!errors.person}
                        helperText={errors.person}
                        getOptionLabel={(option) => {
                            if (!option) return '';
                            const user = option?.user;
                            if (!user) return '';
                            const first = user.first_name?.trim() || '';
                            const last = user.last_name?.trim() || '';
                            const full = `${first} ${last}`.trim();
                            return full || (personType === 'teacher' ? `آموزگار #${option.id}` : `پرسنل #${option.id}`);
                        }}
                        getOptionValue={(option) => option?.user?.id}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="حقوق ماهیانه (تومان)"
                        required
                        type="text"
                        inputMode="numeric"
                        value={formatNumberInputValue(formData.fixed_monthly_salary)}
                        onChange={(e) => {
                            const parsed = parseNumberInput(e.target.value, false); // false = integer only
                            handleChange('fixed_monthly_salary', parsed);
                        }}
                        error={!!errors.fixed_monthly_salary}
                        helperText={errors.fixed_monthly_salary}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <PersianDatePicker
                        label="تاریخ شروع"
                        required
                        value={formData.start_date ? new Date(formData.start_date) : null}
                        onChange={(date) => {
                            if (date && !isNaN(date.getTime())) {
                                handleChange('start_date', date.toISOString().split('T')[0]);
                            } else {
                                handleChange('start_date', '');
                            }
                        }}
                        error={!!errors.start_date}
                        helperText={errors.start_date}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <PersianDatePicker
                        label="تاریخ پایان (اختیاری)"
                        value={formData.end_date ? new Date(formData.end_date) : null}
                        onChange={(date) => {
                            if (date && !isNaN(date.getTime())) {
                                handleChange('end_date', date.toISOString().split('T')[0]);
                            } else {
                                handleChange('end_date', '');
                            }
                        }}
                        error={!!errors.end_date}
                        helperText={errors.end_date || 'در صورت خالی بودن، حقوق تا زمان غیرفعال شدن ادامه دارد'}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.is_active}
                                onChange={(e) => handleChange('is_active', e.target.checked)}
                                color="primary"
                            />
                        }
                        label="فعال"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        فقط یک حقوق فعال برای هر {personType === 'teacher' ? 'آموزگار' : 'پرسنل'} در هر موسسه می‌تواند وجود داشته باشد
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="یادداشت (اختیاری)"
                        multiline
                        rows={3}
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        error={!!errors.notes}
                        helperText={errors.notes}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={onCancel}
                            disabled={isSubmitting}
                        >
                            انصراف
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                        >
                            {isSubmitting ? 'در حال ذخیره...' : (salary ? 'ویرایش' : 'ثبت')}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default TeacherSalaryForm;

